/**
 * BrokerModal Component
 *
 * Modal for creating new brokers.
 * Form with name (required) and default account currency (defaults to USD).
 * Creates broker with brokerReferenceId: undefined (custom broker).
 */

// 1. External dependencies
import React, { useState, useEffect, useCallback } from 'react';
import { Globe } from 'lucide-react';

// 2. Workspace packages
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Alert,
  useToastNotification,
} from '@enfomentis/ui';

// 3. Services
import { brokerService } from '@/services/BrokerService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';

// 4. Internal utilities
import { getCurrencyInfo } from '@/utils';
import { CURRENCY_DATABASE } from '@/constants';

// 5. Shared components
import { CurrencyChooserModal } from '@/components/shared/CurrencyChooser/CurrencyChooserModal';

/**
 * Props for BrokerModal
 */
export interface BrokerModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Callback after successful save - receives new broker ID */
  onSave: (newBrokerId?: string) => void | Promise<void>;
}

/**
 * Form data for broker modal
 */
interface BrokerFormData {
  name: string;
  defaultAccountCurrency: string;
}

/**
 * Initial empty form state
 */
const initialFormData: BrokerFormData = {
  name: '',
  defaultAccountCurrency: 'USD',
};

/**
 * BrokerModal Component
 */
export const BrokerModal: React.FC<BrokerModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  // Form state
  const [formData, setFormData] = useState<BrokerFormData>(initialFormData);
  const [nameError, setNameError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCurrencyChooser, setShowCurrencyChooser] = useState(false);

  // Toast notifications
  const { showToast } = useToastNotification();

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormData);
      setNameError(null);
      setSubmitError(null);
      setShowCurrencyChooser(false);
    }
  }, [isOpen]);

  // Currency display derivations
  const currencyInfo = getCurrencyInfo(formData.defaultAccountCurrency, CURRENCY_DATABASE);
  const currencyDisplay = currencyInfo
    ? `${currencyInfo.symbol ? `${currencyInfo.symbol} ` : ''}${currencyInfo.name} (${formData.defaultAccountCurrency})`
    : formData.defaultAccountCurrency;

  // Close handler — resets currency chooser state before dismissing
  const handleClose = useCallback((): void => {
    setShowCurrencyChooser(false);
    onClose();
  }, [onClose]);

  /**
   * Validate form data
   */
  const validateForm = (): boolean => {
    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      setNameError('Name is required');
      return false;
    }
    setNameError(null);
    return true;
  };

  /**
   * Handle form field changes
   */
  const handleFieldChange = (field: keyof BrokerFormData, value: string): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when field is edited
    if (field === 'name' && nameError) {
      setNameError(null);
    }
  };

  const handleCurrencyChange = useCallback((currencyCode: string): void => {
    setFormData((prev) => ({ ...prev, defaultAccountCurrency: currencyCode }));
    setShowCurrencyChooser(false);
  }, []);

  /**
   * Handle form submission
   */
  const handleSubmit = async (): Promise<void> => {
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    let newBrokerId: string | undefined;
    const trimmedName = formData.name.trim();

    // Stage 1: Create broker (hard failure — modal stays open)
    try {
      setIsSubmitting(true);

      const createData: Parameters<typeof brokerService.add>[0] = {
        name: trimmedName,
        feeMetadata: {
          defaultAccountCurrency: formData.defaultAccountCurrency,
        },
      };

      const newBroker = await brokerService.add(createData);
      newBrokerId = newBroker.id;
      syncCoordinatorService.notifyChange('brokers', newBrokerId);

      showToast({
        title: 'Broker Created',
        description: `"${trimmedName}" has been created`,
        color: 'success',
      });
    } catch (error) {
      console.error('Failed to create broker:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create broker';
      setSubmitError(errorMessage);
      setIsSubmitting(false);
      return;
    }

    // Stage 2: Post-create callback (soft failure — close modal regardless)
    try {
      await onSave(newBrokerId);
    } catch (error) {
      console.error('Post-create callback failed:', error);
      showToast({
        title: 'Refresh Failed',
        description: 'Broker created, but list refresh failed — try switching tabs',
        color: 'warning',
      });
    } finally {
      setIsSubmitting(false);
      handleClose();
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        size="md"
        placement="top-center"
        isDismissable={!isSubmitting}
        hideCloseButton={isSubmitting}
      >
        <ModalContent>
          <ModalHeader>New Broker</ModalHeader>

          <ModalBody className="gap-4">
            {/* Name */}
            <Input
              label="Name"
              placeholder="Enter broker name"
              value={formData.name}
              onValueChange={(value) => handleFieldChange('name', value)}
              isRequired
              isInvalid={!!nameError}
              errorMessage={nameError}
              autoFocus
              className="[&_input]:text-base"
            />

            {/* Default Account Currency */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm text-text-secondary">
                Default Account Currency
              </label>
              <Button
                variant="bordered"
                className="w-full justify-between text-base font-normal"
                onPress={() => setShowCurrencyChooser(true)}
                isDisabled={isSubmitting}
                endContent={
                  <Globe className="w-4 h-4 shrink-0 text-text-tertiary" />
                }
              >
                {currencyDisplay}
              </Button>
            </div>

            {/* Error Alert */}
            {submitError && (
              <Alert color="danger" variant="flat">
                {submitError}
              </Alert>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="flat" onPress={handleClose} isDisabled={isSubmitting}>
              Cancel
            </Button>
            <Button color="primary" onPress={handleSubmit} isLoading={isSubmitting}>
              Create Broker
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <CurrencyChooserModal
        isOpen={isOpen && showCurrencyChooser}
        onClose={() => setShowCurrencyChooser(false)}
        selectedCurrency={formData.defaultAccountCurrency}
        onCurrencySelect={handleCurrencyChange}
        title="Default Account Currency"
      />
    </>
  );
};

export default BrokerModal;
